import { lazy, Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GoRow } from '@/components/GoRow';
import { getCurrentPracticeSession, getProgress, getSignOfTheDay, getWrongCount } from '@/api';
import { Glyph } from '@/components/icons';
import { SignOfDay } from '@/components/SignOfDay';
import { Skeleton } from '@/components/Skeleton';
import { usePullRefresh } from '@/components/PullToRefresh';
import { errorText } from '@/errorText';
import { useAsync, useRefreshable, dataOf } from '@/hooks';
import { useLang } from '@/lang';
import { DEV_PREVIEW } from '@/pro';
import { useSaved } from '@/saved';
import { useSavedSigns } from '@/savedSigns';
import { PlanEnding } from '@/pages/home/PlanEnding';
import { PracticeList, type PracticeRow } from '@/pages/home/PracticeList';
import { RandomSetSheet } from '@/pages/home/RandomSetSheet';
import { useT } from '@/str';
import { SCREEN } from '@/components/layout';
import { cn } from '@/lib/utils';

const AiCards = lazy(() => import('@/pages/home/AiCard').then((m) => ({ default: m.AiCards })));

const thousands = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const CARD = 'bg-card border-border rounded-card flex flex-col gap-2 border px-4 py-3.75';

export function TodayPage() {
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();
  const { ids } = useSaved();
  const { saved: savedSigns } = useSavedSigns();
  const [reload, refresh] = useRefreshable();
  const { ref, pill } = usePullRefresh(refresh);
  const state = useAsync(getProgress, [reload], { keep: true });
  const signState = useAsync(() => getSignOfTheDay(lang), [reload, lang], { keep: true });
  const wrong = useAsync(getWrongCount, [reload], { keep: true });
  const resumable = useAsync(getCurrentPracticeSession, [reload], { keep: true });
  const [pickingSize, setPickingSize] = useState(false);

  if (state.loading || resumable.loading || wrong.loading) {
    return <Skeleton shape="rows" count={7} />;
  }

  const p = state.error === null ? state.data : null;
  const wrongCount = dataOf(wrong);

  const drill = dataOf(resumable);
  const canResume = drill !== null && drill.status === 'in_progress' && drill.questions.length > 0;

  const rows: PracticeRow[] = [
    ...(canResume
      ? [
          {
            key: 'davom',
            label: t.resumeRow,
            sub: drill.label || t.resumeDrillSub,
            icon: <Glyph name="play" size={16} weight={1.9} />,
            tone: 'acc' as const,
            meta: drill.questions.length,
            onClick: () => navigate('/practice/davom'),
          },
        ]
      : []),
    {
      key: 'all',
      label: t.allTests,
      sub: t.allTestsSub,
      icon: <Glyph name="grid" size={16} weight={1.9} />,
      meta: p ? thousands(p.counters.bank_total) : undefined,
      onClick: () => navigate('/practice/all'),
    },
    {
      key: 'topics',
      label: t.byTopic,
      sub: t.byTopicSub,
      icon: <Glyph name="layers" size={16} weight={1.9} />,
      meta: t.qGroups.length,
      onClick: () => navigate('/savollar'),
    },
    {
      key: 'wrong',
      label: t.drill,
      sub: t.drillSub,
      icon: <Glyph name="close" size={16} weight={1.9} />,
      tone: 'bad' as const,
      meta: wrongCount ?? undefined,
      metaBad: wrongCount !== null && wrongCount > 0,
      onClick: () => navigate('/practice/wrong'),
    },
    {
      key: 'saved',
      label: t.saved,
      sub: t.savedSub,
      icon: <span className="text-t14">★</span>,
      tone: 'acc' as const,
      meta: ids.length + savedSigns.length,
      onClick: () => navigate('/saved'),
    },
    {
      key: 'long',
      label: t.longSet,
      sub: t.longSetSub,
      icon: <Glyph name="clock" size={16} weight={1.9} />,
      onClick: () => setPickingSize(true),
    },
    {
      key: 'tricky',
      label: t.trickyRow,
      sub: t.trickyRowSub,
      icon: <Glyph name="bulb" size={16} weight={1.9} />,
      onClick: () => navigate('/practice/tricky'),
    },
    {
      key: 'numeric',
      label: t.numericRow,
      sub: t.numericRowSub,
      icon: '12',
      onClick: () => navigate('/practice/numeric'),
    },
  ];

  const todaysSign = dataOf(signState);

  return (
    <div data-screen className={cn(SCREEN, 'gap-4.5 pt-5.5')} ref={ref}>
      {pill}

      <PlanEnding />

      <PracticeList title={t.grpPractice} rows={rows} />

      {pickingSize && (
        <RandomSetSheet
          onClose={() => setPickingSize(false)}
          onPick={(n) => {
            setPickingSize(false);
            navigate(`/practice/random/${n}`);
          }}
        />
      )}

      {signState.loading ? (
        <section data-block="sod-loading" aria-busy className={cn(CARD, 'gap-3')}>
          <span className="text-t11 text-faint tracking-kicker font-bold uppercase">
            {t.signOfDay}
          </span>
          <span data-skel className="size-16.5" />
          <span data-skel className="min-h-11.5" />
        </section>
      ) : signState.error !== null ? (
        <section data-block="sod-note" className={CARD}>
          <span className="text-t11 text-faint tracking-kicker font-bold uppercase">
            {t.signOfDay}
          </span>
          <span className="text-t15 font-extrabold">{t.errTitle}</span>
          <span className="text-t12h text-muted-foreground">{errorText(signState.error, t)}</span>
          <GoRow
            className="border-border-strong mt-1 py-3"
            onClick={() => void refresh().catch(() => {})}
          >
            {t.retry}
          </GoRow>
        </section>
      ) : todaysSign === null ? (
        <section data-block="sod-note" className={CARD}>
          <span className="text-t11 text-faint tracking-kicker font-bold uppercase">
            {t.signOfDay}
          </span>
          <span className="text-t15 font-extrabold">{t.noSigns}</span>
          <span className="text-t12h text-muted-foreground">{t.noSignsSub}</span>
        </section>
      ) : (
        <SignOfDay key={todaysSign.code} sign={todaysSign} />
      )}

      {DEV_PREVIEW && (
        <Suspense fallback={null}>
          <AiCards />
        </Suspense>
      )}

      <div className="h-1" />
    </div>
  );
}
